/* tslint:disable:max-line-length */
import {Inject, Injectable, OnDestroy} from '@angular/core';
import {SmagApiService} from '../api/smag-api.service';
import {Notification, Severity, STATUSES} from 'smag-commons/dto/msg';
import {Observable, of, Subject} from 'rxjs';
import {APP_CONFIG_TOKEN, AppConfig} from '../app.config';
import {catchError, map} from 'rxjs/operators';
import * as _ from 'lodash';
import {I18NService} from '../i18n/i18n.service';
import {MsgBoxService} from './msg-box.service';
import {NetworkService} from './network.service';
import {TimeFilter} from './time-filter/time-filter.component';

export interface ViewNotification {
    not: Notification;
    hasDetails?: boolean;
    correlatedObject?: any;
}

const EC_STATUSES = [
    STATUSES.EC_STATISTICS_FAIL,
    STATUSES.EC_MISSING_CONSUMPTION_ADVICE,
    STATUSES.EC_WEEKLY_PERFORMANCE_FAIL,
    STATUSES.EC_CONSUMPTION_ADVICE_SEND_FAIL,
    STATUSES.EC_WEEKLY_PERFORMANCE_SEND_FAIL
];

/**
 * Service which provides notification-related functionalities.
 */
@Injectable()
export class NotificationsService implements OnDestroy {

    private destroyed = new Subject<void>();

    constructor(@Inject(APP_CONFIG_TOKEN) private readonly cfg: AppConfig,
                private readonly apiService: SmagApiService,
                private readonly networkService: NetworkService,
                private readonly i18n: I18NService,
                private readonly msgBoxService: MsgBoxService) {
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    getECNotifications(networkId: string,
                       timeFilter: TimeFilter,
                       severities: Severity[]): Observable<ViewNotification[]> {
        const {fromTime, toTime} = timeFilter.range();
        return this.apiService.retrieveNotifications([networkId],
            null,
            null,
            EC_STATUSES,
            severities,
            fromTime,
            toTime,
            null).pipe(
            map(res => res.results),
            catchError(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot retrieve notifications'), err);
                return of([] as Notification[]); // still wait for real-time notifications
            }),
            map(nots => keepLatests(nots)), // keep only the latest notification for each correlation id
            map(nots => _.sortBy(nots, n => -n.timestamp)), // descending order
            map(nots => nots.map(n => ({
                not: n,
                hasDetails: hasDetails(n)
            })))
        );
    }

    loadDetails(correlationId: string): Observable<ViewNotification[]> {
        // load all notifications with the given correlation id (including the "original" one)
        return this.apiService.retrieveNotificationDetails(correlationId).pipe(
            map(details => details.results.map(detail => ({
                not: detail.notification,
                correlatedObject: detail.correlated_object
            } as ViewNotification))),
            catchError(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot retrieve notification details'), err);
                throw err;
            })
        );
    }
}

function keepLatests(notifications: Notification[]): Notification[] {
    const nots: Notification[] = [];
    const notGroups = _.groupBy(notifications, n => n.correlation_id || '___nocid___');
    for (let cid of Object.keys(notGroups)) {
        if (cid === '___nocid___') {
            // keep all notifications without a correlations id
            nots.push(...notGroups[cid]);
        } else {
            // keep only the most recent notification with a given correlation id
            nots.push(_.sortBy(notGroups[cid], n => -n.timestamp)[0]);
        }
    }
    return nots;
}

function hasDetails(not: Notification): boolean {
    return !!not.correlation_id;
}
